import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import {
  CALIBRATION_TOOL_SNAPPING_ANGLE_STEP,
  VIEWER_TOP_OFFSET,
  SnappingIndicatorStyle,
} from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import {
  angleBetweenTwoPoints,
  endPointByAngle,
  snapAngle,
} from 'src/components/viewers/SheetsViewerV2/utils/cartesian';
import { getSnapPointIndicator } from 'src/components/viewers/SheetsViewerV2/utils/snapping';
import { ChakraColor } from 'src/utils/color-util';
import Edit2D = Autodesk.Extensions.Edit2D;

// Screen-space snapping indicator for CalibrationTool
class CalibrationSnappingIndicator {
  private element: HTMLDivElement;
  private viewer: AggregatedView['viewer'];
  private snapPoint: THREE.Vector3 | null = null;

  constructor(viewer: AggregatedView['viewer']) {
    this.viewer = viewer;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1001';
    this.element.style.boxSizing = 'border-box';
    this.element.style.width = `${SnappingIndicatorStyle.size}px`;
    this.element.style.height = `${SnappingIndicatorStyle.size}px`;
    this.element.style.border = `${SnappingIndicatorStyle.borderWidth}px solid ${SnappingIndicatorStyle.borderColor}`;
    this.element.style.backgroundColor = SnappingIndicatorStyle.backgroundColor;
    this.element.style.display = 'none';

    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }
  }

  public setSnapPoint(point: THREE.Vector3 | null): void {
    this.snapPoint = point;
    if (point) {
      this.element.style.display = 'block';
      this.updatePosition();
    } else {
      this.element.style.display = 'none';
    }
  }

  public getSnapPoint(): THREE.Vector3 | null {
    return this.snapPoint;
  }

  private updatePosition(): void {
    if (!this.snapPoint) return;

    const projected = Autodesk.Viewing.MeasureCommon.project(
      this.snapPoint,
      this.viewer
    );

    const halfSize = SnappingIndicatorStyle.size / 2;
    this.element.style.left = `${projected.x - halfSize}px`;
    this.element.style.top = `${projected.y - halfSize}px`;
  }

  public update(): void {
    if (this.snapPoint) {
      this.updatePosition();
    }
  }

  public remove(): void {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Screen-space endpoint handle for dragging
class EndpointHandle {
  private element: HTMLDivElement;
  private viewer: AggregatedView['viewer'];
  private point: THREE.Vector3 | null = null;
  private isDragging = false;
  private onDrag: ((newPoint: THREE.Vector3) => void) | null = null;
  private onDragEnd: (() => void) | null = null;
  private static readonly SIZE = 10;
  private static readonly BORDER_WIDTH = 2;

  // Bound event handlers for proper cleanup
  private _onMouseDown: (e: MouseEvent) => void;
  private _onMouseMove: (e: MouseEvent) => void;
  private _onMouseUp: (e: MouseEvent) => void;

  constructor(viewer: AggregatedView['viewer']) {
    this.viewer = viewer;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.zIndex = '1002';
    this.element.style.boxSizing = 'border-box';
    this.element.style.width = `${EndpointHandle.SIZE}px`;
    this.element.style.height = `${EndpointHandle.SIZE}px`;
    this.element.style.borderRadius = '50%';
    this.element.style.border = `${EndpointHandle.BORDER_WIDTH}px solid ${ChakraColor.yellow500}`;
    this.element.style.backgroundColor = ChakraColor.gray50;
    this.element.style.cursor = 'move';
    this.element.style.display = 'none';

    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }

    // Bind event handlers
    this._onMouseDown = this.handleMouseDown.bind(this);
    this._onMouseMove = this.handleMouseMove.bind(this);
    this._onMouseUp = this.handleMouseUp.bind(this);

    // Add mousedown listener to the element
    this.element.addEventListener('mousedown', this._onMouseDown);
  }

  public setDragHandlers(
    onDrag: (newPoint: THREE.Vector3) => void,
    onDragEnd: () => void
  ): void {
    this.onDrag = onDrag;
    this.onDragEnd = onDragEnd;
  }

  private handleMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return; // Only left click
    e.preventDefault();
    e.stopPropagation();

    this.isDragging = true;

    // Add window-level listeners for move and up
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();

    const clientX = e.clientX;
    const clientY = e.clientY - VIEWER_TOP_OFFSET;
    const newPoint = this.viewer.impl.intersectGround(clientX, clientY);

    if (newPoint && this.onDrag) {
      this.onDrag(newPoint);
    }
  }

  private handleMouseUp(e: MouseEvent): void {
    if (!this.isDragging) return;
    e.preventDefault();

    this.isDragging = false;

    // Remove window-level listeners
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mouseup', this._onMouseUp);

    if (this.onDragEnd) {
      this.onDragEnd();
    }
  }

  public setPoint(point: THREE.Vector3 | null): void {
    this.point = point;
    if (point) {
      this.element.style.display = 'block';
      this.updatePosition();
    } else {
      this.element.style.display = 'none';
    }
  }

  public getPoint(): THREE.Vector3 | null {
    return this.point;
  }

  private updatePosition(): void {
    if (!this.point) return;

    const projected = Autodesk.Viewing.MeasureCommon.project(
      this.point,
      this.viewer
    );

    const halfSize = EndpointHandle.SIZE / 2;
    this.element.style.left = `${projected.x - halfSize}px`;
    this.element.style.top = `${projected.y - halfSize}px`;
  }

  public update(): void {
    if (this.point) {
      this.updatePosition();
    }
  }

  public remove(): void {
    // Clean up event listeners
    this.element.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mouseup', this._onMouseUp);

    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

// Calibration line style for Edit2D
const CalibrationLineStyle = {
  lineWidth: 2,
  lineColor: ChakraColor.yellow500,
};

const CalibrationPreviewLineStyle = {
  lineWidth: 2,
  lineColor: ChakraColor.yellow300,
};

type CalibrationPointsChangedHandler = (
  pointA: THREE.Vector3 | null,
  pointB: THREE.Vector3 | null
) => void;

const CROSSHAIR_CURSOR = 'crosshair';

export class CalibrationTool extends Autodesk.Viewing.ToolInterface {
  names = [ActiveTool.Calibration];

  private snappingIndicator: CalibrationSnappingIndicator | null = null;
  private endpointA: EndpointHandle | null = null;
  private endpointB: EndpointHandle | null = null;
  private lineShape: Autodesk.Edit2D.Polyline | null = null;
  private _cameraChangeHandler: (() => void) | null = null;
  private _keyDownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(
    private readonly viewer: AggregatedView['viewer'],
    private readonly snapper: Autodesk.Viewing.Extensions.Snapping.Snapper,
    private readonly edit2d: Edit2D,
    readonly onChange: CalibrationPointsChangedHandler,
    public pointA: THREE.Vector3 | null,
    public pointB: THREE.Vector3 | null
  ) {
    super();

    delete (this as any).handleSingleClick;
    delete (this as any).handleMouseMove;
    delete (this as any).activate;
    delete (this as any).deactivate;
  }

  getCursor(): string {
    return CROSSHAIR_CURSOR;
  }

  activate(): void {
    this.snapper.activate();
    this.snappingIndicator = new CalibrationSnappingIndicator(this.viewer);
    this.endpointA = new EndpointHandle(this.viewer);
    this.endpointB = new EndpointHandle(this.viewer);

    // Set up drag handlers for endpoint A
    this.endpointA.setDragHandlers(
      (newPoint) => {
        this.pointA = newPoint;
        this.endpointA?.setPoint(this.pointA);
        if (this.pointB) {
          this._drawLine(this.pointA, this.pointB, false);
          this.onChange(this.pointA, this.pointB);
        }
      },
      () => {
        // Drag end - nothing special needed
      }
    );

    // Set up drag handlers for endpoint B
    this.endpointB.setDragHandlers(
      (newPoint) => {
        this.pointB = newPoint;
        this.endpointB?.setPoint(this.pointB);
        if (this.pointA) {
          this._drawLine(this.pointA, this.pointB, false);
          this.onChange(this.pointA, this.pointB);
        }
      },
      () => {
        // Drag end - nothing special needed
      }
    );

    // Draw initial line if points exist
    if (this.pointA && this.pointB) {
      this._drawLine(this.pointA, this.pointB, false);
      this.endpointA.setPoint(this.pointA);
      this.endpointB.setPoint(this.pointB);
    }

    // Update elements on camera change
    this._cameraChangeHandler = () => {
      this.snappingIndicator?.update();
      this.endpointA?.update();
      this.endpointB?.update();
    };
    this.viewer.addEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._cameraChangeHandler
    );

    // ESC key handler to clear
    this._keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this._clearAll();
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', this._keyDownHandler, true);
  }

  deactivate(): void {
    this.snapper.deactivate();
    this._removeLine();

    if (this.snappingIndicator) {
      this.snappingIndicator.remove();
      this.snappingIndicator = null;
    }
    if (this.endpointA) {
      this.endpointA.remove();
      this.endpointA = null;
    }
    if (this.endpointB) {
      this.endpointB.remove();
      this.endpointB = null;
    }
    if (this._cameraChangeHandler) {
      this.viewer.removeEventListener(
        Autodesk.Viewing.CAMERA_CHANGE_EVENT,
        this._cameraChangeHandler
      );
      this._cameraChangeHandler = null;
    }
    if (this._keyDownHandler) {
      window.removeEventListener('keydown', this._keyDownHandler, true);
      this._keyDownHandler = null;
    }
  }

  private _getCursorPosition(event: MouseEvent): THREE.Vector3 | null {
    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    const snapPoint = getSnapPointIndicator(this.snapper, this.viewer);
    this.snappingIndicator?.setSnapPoint(snapPoint);

    return this.viewer.impl.intersectGround(clientX, clientY);
  }

  private _getSnappedPosition(rawPoint: THREE.Vector3): THREE.Vector3 {
    const snapPoint = this.snappingIndicator?.getSnapPoint();
    return snapPoint ? snapPoint : rawPoint;
  }

  handleSingleClick(event: MouseEvent, button: number): boolean {
    const cursorPositionRaw = this._getCursorPosition(event);
    if (!cursorPositionRaw) return false;

    const cursorPosition = this._getSnappedPosition(cursorPositionRaw);

    if (button === 2) return this._handleRightClick();
    if (button !== 0) return false;

    if (!this.pointA || this.pointB) {
      // Starting new line or restarting
      this.pointA = cursorPosition;
      this.pointB = null;
      this._removeLine();
      this.endpointA?.setPoint(this.pointA);
      this.endpointB?.setPoint(null);
      this.onChange(this.pointA, null);
    } else if (this.pointA && !this.pointB) {
      // Completing the line
      this.pointB = this._calculateEndPoint(cursorPosition, event.shiftKey);
      this._drawLine(this.pointA, this.pointB, false);
      this.endpointA?.setPoint(this.pointA);
      this.endpointB?.setPoint(this.pointB);
      this.onChange(this.pointA, this.pointB);
    }

    return true;
  }

  handleMouseMove(event: MouseEvent): boolean {
    const cursorPositionRaw = this._getCursorPosition(event);
    if (!cursorPositionRaw) return false;

    // Preview line while drawing
    if (!this.pointB && this.pointA) {
      const targetPoint = this._calculateEndPoint(
        cursorPositionRaw,
        event.shiftKey
      );
      this._drawLine(this.pointA, targetPoint, true);
    }

    return false;
  }

  private _handleRightClick(): boolean {
    if (this.pointB) {
      this.pointB = null;
      this._removeLine();
      this.endpointB?.setPoint(null);
      this.onChange(this.pointA, this.pointB);
      return true;
    } else if (this.pointA) {
      this._clearAll();
      return true;
    }

    return false;
  }

  private _clearAll(): void {
    this.pointA = null;
    this.pointB = null;
    this._removeLine();
    this.endpointA?.setPoint(null);
    this.endpointB?.setPoint(null);
    this.snappingIndicator?.setSnapPoint(null);
    this.onChange(null, null);
  }

  private _calculateEndPoint(
    cursorPosition: THREE.Vector3,
    shiftKey: boolean
  ): THREE.Vector3 {
    if (!this.pointA) {
      throw new Error('Cannot calculate end point without start point');
    }

    const angle = angleBetweenTwoPoints([this.pointA, cursorPosition]);
    const snappedAngle = snapAngle(angle, CALIBRATION_TOOL_SNAPPING_ANGLE_STEP);

    if (shiftKey) {
      return endPointByAngle(
        this.pointA,
        snappedAngle,
        this.pointA.distanceTo(cursorPosition)
      );
    } else {
      return cursorPosition;
    }
  }

  private _drawLine(
    startPoint: THREE.Vector3,
    endPoint: THREE.Vector3,
    isPreview: boolean
  ): void {
    this._removeLine();

    const points = [
      { x: startPoint.x, y: startPoint.y },
      { x: endPoint.x, y: endPoint.y },
    ];

    this.lineShape = new Autodesk.Edit2D.Polyline(points);

    const style = isPreview
      ? CalibrationPreviewLineStyle
      : CalibrationLineStyle;
    this.lineShape.style.lineWidth = style.lineWidth;
    this.lineShape.style.lineColor = style.lineColor;

    this.edit2d.defaultContext.layer.addShapes([this.lineShape]);
  }

  private _removeLine(): void {
    if (this.lineShape) {
      this.edit2d.defaultContext.layer.removeShapes([this.lineShape]);
      this.lineShape = null;
    }
  }
}
